<template>
  <div class="flex flex-col gap-6 px-14">
    <!-- Datos del administrador -->
    <div class="flex-row flex justify-between">
      <TitlePrimary :title="$t('admin.title')" class="px-8" />
      <div class="flex items-center gap-2">
        <RouterLink :to="{ path: '/new-admin', query: { id: id, action: 'edit' } }">
          <ButtonWhite>{{ $t('user.buttons.edit') }}</ButtonWhite>
        </RouterLink>
        <ButtonWhite @click="confirmAndSendSetPassword" class="bg-white" :title="$t('admin.buttons.changePassword') || 'Cambiar contraseña'">
          {{ $t('admin.buttons.changePassword') || 'Cambiar contraseña' }}
        </ButtonWhite>
      </div>
    </div>

    <div v-if="loading" class="text-center p-4">{{ $t('admin.loading') }}</div>
    <div v-else-if="error" class="text-red-500 text-center p-4">{{ error }}</div>
    <div v-else-if="userData" class="grid-container p-8 rounded-xl border shadow-[0px_4px_4px_0px_rgba(0,0,0,0.25)]">
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <div>
          <InputPrimary 
            :modelValue="userData.name ?? '-'" 
            class="max-w-[350px]" 
            type="text" 
            :label="$t('admin.labels.accountName') + ':'" 
          />
          <InputPrimary 
            :modelValue="userData.cif ?? '-'" 
            class="max-w-[350px]" 
            type="text" 
            :label="$t('admin.labels.cif') + ':'" 
          />
          <!-- Representante legal de la cuenta (ADS-0121) -->
          <InputPrimary
            :modelValue="userData.legal_rep_name || '-'"
            class="max-w-[350px]"
            type="text"
            :label="$t('admin.labels.legalRepName') + ':'"
          />
          <InputPrimary
            :modelValue="userData.legal_rep_surname || '-'"
            class="max-w-[350px]"
            type="text"
            :label="$t('admin.labels.legalRepSurname') + ':'"
          />
          <InputPrimary
            :modelValue="userData.legal_rep_document || '-'"
            class="max-w-[350px]"
            type="text"
            :label="$t('admin.labels.legalRepDocument') + ':'"
          />
          <InputPrimary 
            :modelValue="userData.mobile ?? '-'" 
            class="max-w-[350px]" 
            type="text" 
            :label="$t('admin.labels.phone') + ':'" 
          />
          <InputPrimary 
            :modelValue="userData.address ?? '-'" 
            class="max-w-[350px]" 
            type="text" 
            :label="$t('admin.labels.address') + ':'" 
          />
          <InputPrimary 
            :modelValue="userData.postal_code ?? '-'" 
            class="max-w-[350px]" 
            type="text" 
            :label="$t('admin.labels.postalCode') + ':'" 
          />
        </div>
        <div>
          <InputPrimary 
            :modelValue="userData.city ?? '-'" 
            class="max-w-[350px]" 
            type="text" 
            :label="$t('admin.labels.municipality') + ':'" 
          />
          <InputPrimary 
            :modelValue="userData.state ?? '-'" 
            class="max-w-[350px]" 
            type="text" 
            :label="$t('admin.labels.province') + ':'" 
          />
          <InputPrimary 
            :modelValue="accountTypeString" 
            class="max-w-[350px]" 
            type="text" 
            :label="$t('admin.labels.accountType') + ':'" 
          />
          <InputPrimary 
            :modelValue="boolToSiNo(userData.contract_data)" 
            class="max-w-[350px]" 
            type="text" 
            :label="$t('admin.labels.dataContract') + ':'" 
          />
          <InputPrimary 
            :modelValue="boolToSiNo(userData.has_notifications)" 
            class="max-w-[350px]" 
            type="text" 
            :label="$t('admin.labels.notifications') + ':'" 
          />
          <InputPrimary
            :modelValue="boolToSiNo(userData.sees_all_requests)"
            class="max-w-[350px]"
            type="text"
            :label="$t('admin.labels.seesAllRequests') + ':'"
          />
        </div>
        <div>
          <InputPrimary 
            :modelValue="boolToSiNo(userData.maintenance)" 
            class="max-w-[350px]" 
            type="text" 
            :label="$t('admin.labels.maintenance') + ':'" 
          />
          <InputPrimary 
            :modelValue="userData.commercial_label ?? userData.commercial ?? '-'"
            class="max-w-[350px]" 
            type="text" 
            :label="$t('admin.labels.commercial') + ':'" 
          />
          <!-- <InputPrimary 
            :modelValue="userData.bono ?? '-'" 
            class="max-w-[350px]" 
            type="text" 
            :label="$t('admin.labels.remainingBonus') + ':'" 
          /> -->
        </div>
      </div>
    </div>

    <div class="grid-container rounded-xl border shadow-[0px_4px_4px_0px_rgba(0,0,0,0.25)]">
      <div class="flex flex-col w-full items-start justify-center gap-4 p-8">
        <label for="client-observations" class="cursor-pointer text-[#385660]">
          {{ $t('admin.labels.observations') }}:
        </label>
        <textarea
          id="client-observations"
          v-model="observations"
          :readonly="!canEditObservations"
          maxlength="1000"
          :aria-describedby="canEditObservations ? 'observations-status' : undefined"
          class="text-[#7CADB9] border border-[#3A5762] rounded-xl h-29 p-2 resize-none w-full outline-none shadow-[0px_4px_4px_0px_rgba(0,0,0,0.25)]">
        </textarea>
        <div class="flex w-full flex-wrap items-center justify-between gap-3">
          <span class="text-xs text-[#7CADB9]">{{ observations.length }}/1000</span>
          <ButtonWhite
            v-if="canEditObservations"
            class="w-auto disabled:cursor-not-allowed disabled:opacity-60"
            :disabled="savingObservations || !observationsChanged"
            @click="saveObservations"
          >
            {{ savingObservations ? $t('admin.observations.saving') : $t('admin.observations.save') }}
          </ButtonWhite>
        </div>
        <p
          v-if="observationsStatus"
          id="observations-status"
          aria-live="polite"
          class="text-sm"
          :class="observationsError ? 'text-red-600' : 'text-green-700'"
        >
          {{ observationsStatus }}
        </p>
      </div>
    </div>

    <!-- Comentarios comerciales internos (ADS-0181): solo los ve el personal de ADS -->
    <div v-if="canEditComments" class="grid-container rounded-xl border shadow-[0px_4px_4px_0px_rgba(0,0,0,0.25)]">
      <div class="flex flex-col w-full items-start justify-center gap-4 p-8">
        <label for="client-comments" class="cursor-pointer text-[#385660]">
          {{ $t('admin.labels.comments') }}:
        </label>
        <p id="comments-hint" class="text-sm text-[#7CADB9]">{{ $t('admin.comments.hint') }}</p>
        <textarea
          id="client-comments"
          v-model="comments"
          maxlength="2000"
          aria-describedby="comments-hint comments-status"
          class="text-[#7CADB9] border border-[#3A5762] rounded-xl h-29 p-2 resize-none w-full outline-none shadow-[0px_4px_4px_0px_rgba(0,0,0,0.25)]">
        </textarea>
        <div class="flex w-full flex-wrap items-center justify-between gap-3">
          <span class="text-xs text-[#7CADB9]">{{ comments.length }}/2000</span>
          <ButtonWhite
            class="w-auto disabled:cursor-not-allowed disabled:opacity-60"
            :disabled="savingComments || !commentsChanged"
            @click="saveComments"
          >
            {{ savingComments ? $t('admin.comments.saving') : $t('admin.comments.save') }}
          </ButtonWhite>
        </div>
        <p
          v-if="commentsStatus"
          id="comments-status"
          aria-live="polite"
          class="text-sm"
          :class="commentsError ? 'text-red-600' : 'text-green-700'"
        >
          {{ commentsStatus }}
        </p>
      </div>
    </div>

    <!-- Usuarios -->
    <div>
      <div class="flex-row flex justify-between gap-4">
        <TitlePrimary :title="$t('admin.labels.users')" class="px-8" />
        <RouterLink :to="{ path: '/user-new', query: { admin_id: id } }">
          <ButtonWhite class="" :icon="PlusIcon" :title="$t('admin.buttons.addNew')" />
        </RouterLink>
      </div>

      <div class="pt-4">
        <TableComponent :fields="users" :items="itemsUsersProcessed" />
        <PaginateComponent 
          :totalPages="totalUsersPages" 
          :modelValue="currentUsersPage"
          @update:modelValue="handlePageChangeUsers" 
        />
      </div>
    </div>

    <!-- Archivos -->
    <div>
      <TitlePrimary :title="$t('admin.labels.files')" class="px-8" />
      <div class="pt-4">
        <div class="overflow-x-auto">
          <!-- hidden file input for uploads -->
          <input ref="fileInputRef" type="file" class="hidden" @change="onFileSelected" />
          <table class="w-full table-auto border-separate min-w-[900px]">
            <thead>
              <tr class="bg-gray-100">
                <th class="px-2 py-2 text-left text-xs font-medium text-[#42A9C2] bg-white uppercase tracking-wider">{{ $t('admin.labels.fichero') || 'Fichero' }}</th>
                <th class="px-2 py-2 text-left text-xs font-medium text-[#42A9C2] bg-white uppercase tracking-wider">{{ $t('admin.labels.date') || 'Fecha' }}</th>
                <th class="px-2 py-2 text-left text-xs font-medium text-[#42A9C2] bg-white uppercase tracking-wider">{{ $t('admin.labels.name') || 'Nombre' }}</th>
                <th class="px-2 py-2 text-left text-xs font-medium text-[#42A9C2] bg-white uppercase tracking-wider">{{ $t('admin.labels.action') || 'Acción' }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="!itemsFilesProcessed.length">
                <td class="px-4 py-3 text-center text-[#7CADB9]" colspan="4">{{ $t('admin.table.noFiles') || 'No hay archivos' }}</td>
              </tr>
              <tr v-for="(file, idx) in itemsFilesProcessed" :key="file.categoryKey ?? idx" class="bg-white shadow-[0_0_0_1px_#385660] rounded-lg">
                <td class="px-2 py-2 text-sm text-gray-500">{{ file.fichero }}</td>
                <td class="px-2 py-2 text-sm text-gray-500">{{ file.fecha }}</td>
                <td class="px-2 py-2 text-sm text-gray-500">{{ file.name }}</td>
                <td class="px-2 py-2 text-sm text-gray-500">
                  <div class="flex items-center gap-2">
                            <ButtonWhite v-if="file.file_path" @click="() => openFile(file)" :title="$t('admin.labels.view') || 'Ver'" />
                            <ButtonWhite @click="() => triggerFileInput(file.categoryKey)" :title="file.accion" />
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <PaginateComponent 
          :totalPages="totalFilesPages" 
          :modelValue="currentFilesPage"
          @update:modelValue="handlePageChangeFiles" 
        />
      </div>
    </div>

    <!-- Bonos -->
    <div class="py-4">
      <div class="flex-row flex justify-between gap-4">
        <TitlePrimary :title="$t('admin.labels.bonuses')" class="px-8" />
        <div class="flex flex-row items-center justify-between py-2">
          <div class="flex flex-row items-center gap-2">
            <ExportFetch 
              exportEndpoint="/liquidation/user/export" 
              :fileName="$t('admin.labels.bonuses')"
              @export-started="handleExportStarted"
              @export-completed="handleExportCompleted"
              @export-failed="handleExportFailed"
            />
            <PrintTableComponent
              :title="$t('admin.labels.bonuses')"
              :fields="bonos"
              :items="itemsBonos"
              @print-started="() => console.log($t('admin.table.print.started'))"
              @print-completed="() => console.log($t('admin.table.print.completed'))"
              @print-failed="(error) => console.error($t('admin.table.print.failed'), error)"
            />
          </div>
          <ButtonWhite class="flex items-center gap-2" :icon="PlusIcon" :title="$t('admin.buttons.addNew')" />
        </div>
      </div>

      <div class="pt-4">
        <div v-if="itemsBonos && itemsBonos.length">
          <TableComponent 
            :fields="bonos" 
            :items="itemsBonos" 
            :showOptions="true" 
            route="/detail-admin" 
          />
        </div>
        <div v-else class="p-4 text-center text-[#7CADB9]">
          {{ $t('admin.labels.noBonuses') || 'No hay bonos' }}
        </div>
        <PaginateComponent 
          :totalPages="totalBonosPages" 
          :modelValue="currentBonosPage"
          @update:modelValue="handlePageChangeBonos" 
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import { onBeforeUnmount, onMounted, ref, computed } from 'vue';
import axios from 'axios';
import { onBeforeRouteLeave, useRoute } from 'vue-router';
import { useExportStore } from '@/stores/exportStore';
import { useI18n } from 'vue-i18n';

import TitlePrimary from '@/components/title/title-primary.vue';
import InputPrimary from '@/components/inputs/input-primary.vue';
import ButtonWhite from '@/components/button/ButtonWhite.vue';
import ExportFetch from '@/components/fetch/exportFetch.vue';
import TableComponent from '@/components/table/TableComponent.vue';
import PaginateComponent from '@/components/paginate/PaginateComponent.vue';
import PrintTableComponent from '@/components/print/PrintTableComponent.vue';

import PlusIcon from '@/assets/svg/PlusIcon.vue';
import { toast } from 'vue3-toastify';
import { accountTypeLabel, fetchAccountDetailWithKind } from '@/services/clientAccount';
import { isAdsUser } from '@/services/userRoles';
import { useAccountTextField } from '@/composables/useAccountTextField';

const route = useRoute();
const id = route.params.id;
const exportStore = useExportStore();
const { t } = useI18n();

const itemsUsers = ref([]);
const currentUsersPage = ref(1);
const totalUsersPages = ref(1);

const itemsFiles = ref([]);
const currentFilesPage = ref(1);
const totalFilesPages = ref(1);

const itemsBonos = ref([]);
const currentBonosPage = ref(1);
const totalBonosPages = ref(1);

const userData = ref(null);
const detailKind = ref(null);
const loading = ref(true);
const error = ref(null);

const users = ref([
  { key: 'id', label: 'Id' },
  { key: 'name', label: 'Nombre' },
  { key: 'email', label: 'Correo' },
  { key: 'city', label: 'Provincia' },
  { key: 'type', label: 'Perfil' },
  { key: 'status', label: 'Estado' },
]);
// File categories to always show (key, display label)
const fileCategories = [
  { key: 'nif', label: 'NIF/CIF' },
  { key: 'autorizacion', label: 'Autorización' },
  { key: 'multipunto', label: 'Multipunto' },
];
const bonos = ref([
  { key: 'bono', label: 'Bono' },
  { key: 'fechadealta', label: 'Fecha de alta' },
  { key: 'fechadebaja', label: 'Fecha de baja' },
  { key: 'opciones', label: 'Opciones' },
]);

const userFetchDetail = async () => {
    if (!id) {
        error.value = 'No ID provided in route.';
        loading.value = false;
        return;
    }
    loading.value = true;
    error.value = null;
    try {
        const detail = await fetchAccountDetailWithKind(id);
        const account = detail.value;
        if (account) {
          userData.value = account;
          detailKind.value = detail.kind;
          resetObservations(account);
          resetComments(account);
          // Las personas de la cuenta vienen de `account_user`, ya agrupadas.
          itemsUsers.value = account.children ?? [];
          // Reset pagination for the local extracted list
          totalUsersPages.value = 1;
          currentUsersPage.value = 1;
          // Extract files from the returned user data (my_files) for the files table
          itemsFiles.value = account.my_files ?? [];
          // Reset files pagination for the local extracted list
          totalFilesPages.value = 1;
          currentFilesPage.value = 1;
        } else {
            userData.value = null;
            detailKind.value = null;
            error.value = 'No se encontraron datos para esta Comunidades.';
        }
    } catch (err) {
        console.error('Error fetching Comunidades detail:', err);
        error.value = 'Failed to load Comunidades details.';
        userData.value = null;
        detailKind.value = null;
    } finally {
        loading.value = false;
    }
};

onMounted(() => {
  userFetchDetail();
  window.addEventListener('beforeunload', warnBeforeUnload);
});

onBeforeUnmount(() => {
  window.removeEventListener('beforeunload', warnBeforeUnload);
});

const sendSetPassword = async () => {
  if (!id) {
    toast.error('ID de usuario no disponible', { position: 'top-right' });
    return;
  }
  try {
    const resp = await axios.post(
      `${import.meta.env.VITE_API_URL}/client-account/send-set-password/${id}`,
      {},
      {
        headers: {
          'Accept': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      }
    );

    if (resp?.data) {
      toast.success(t('admin.messages.passwordEmailSent') || 'Correo de cambio de contraseña enviado', { position: 'top-right' });
    } else {
      toast.error('No se pudo enviar el correo de cambio de contraseña', { position: 'top-right' });
    }
  } catch (err) {
    console.error('Error sending set-password:', err);
    toast.error(err.response?.data?.value || t('admin.messages.passwordEmailSendFailed'), { position: 'top-right' });
  }
};

const confirmAndSendSetPassword = async () => {
  const message = t('admin.confirmations.sendPassword');
  const confirmMsg = message || 'Se enviará un correo para cambiar la contraseña. ¿Desea continuar?';

  let proceed = true;
  try {
    proceed = window.confirm(confirmMsg);
  } catch {
    toast.info(confirmMsg, { position: 'top-right' });
    proceed = true;
  }

  if (!proceed) return;

  await sendSetPassword();
};

// const fetchUsers = async () => {
//   try {
//     const response = await axios.get(
//       `${import.meta.env.VITE_API_URL}/admin/list`,
//       {
//         headers: {
//           'Accept': 'application/json',
//           'Authorization': `Bearer ${localStorage.getItem('token')}`
//         },
//         params: {
//           per_page: 10,
//           page: currentUsersPage.value
//         }
//       }
//     );
//     itemsUsers.value = response.data.value.data;
//     totalUsersPages.value = response.data.value.last_page;
//   } catch (error) {
//     itemsUsers.value = [];
//     console.error('Error fetching data:', error);
//   }
// };
// const fetchFiles = async () => {
//   try {
//     const response = await axios.get(
//       `${import.meta.env.VITE_API_URL}/admin/list`,
//       {
//         headers: {
//           'Accept': 'application/json',
//           'Authorization': `Bearer ${localStorage.getItem('token')}`
//         },
//         params: {
//           per_page: 10,
//           page: currentFilesPage.value
//         }
//       }
//     );
//     itemsFiles.value = response.data.value.data;
//     totalFilesPages.value = response.data.value.last_page;
//   } catch (error) {
//     itemsFiles.value = [];
//     console.error('Error fetching data:', error);
//   }
// };
// const fetchBonos = async () => {
//   try {
//     const response = await axios.get(
//       `${import.meta.env.VITE_API_URL}/admin/list`,
//       {
//         headers: {
//           'Accept': 'application/json',
//           'Authorization': `Bearer ${localStorage.getItem('token')}`
//         },
//         params: {
//           per_page: 10,
//           page: currentBonosPage.value
//         }
//       }
//     );
//     itemsBonos.value = response.data.value.data;
//     totalBonosPages.value = response.data.value.last_page;
//   } catch (error) {
//     itemsBonos.value = [];
//     console.error('Error fetching data:', error);
//   }
// };

onMounted(() => {
  // fetchUsers();
  // fetchFiles();
  // fetchBonos();
});

const itemsUsersProcessed = computed(() => {
  return itemsUsers.value.map(user => {
    // Check if roles array exists and has items
    // `role_label` trae el rol de la persona dentro de la cuenta con la
    // nomenclatura del panel legacy, incluido "(Colaborador)" para el
    // usuario secundario. Los roles crudos quedan como respaldo.
    const roleNames = user.role_label
      || (Array.isArray(user.roles) ? user.roles.map(role => role.name).filter(Boolean).join(', ') : '')
      || '-';
    return {
      ...user,
      // Provide the roles string under the `type` key so the table's
      // `type` field (Perfil) shows the role(s) as account type.
      type: roleNames
    };
  });
});

const formatDateEU = (iso) => {
  if (!iso) return '-';
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    return `${day}/${month}/${year}`;
  } catch {
    return iso;
  }
};

// Convert various truthy/falsy representations to Spanish Sí/No
const boolToSiNo = (v) => {
  if (v === 1 || v === '1' || v === true) return 'Sí';
  if (v === 0 || v === '0' || v === false) return 'No';
  // if null/undefined keep placeholder
  if (v === null || v === undefined) return '-';
  // fallback: if it's a string like 'true'/'false'
  const lower = String(v).toLowerCase();
  if (lower === 'true') return 'Sí';
  if (lower === 'false') return 'No';
  return String(v);
};

const itemsFilesProcessed = computed(() => {
  const mapFileTypeToCategory = (ft) => {
    if (!ft) return null;
    const lower = String(ft).toLowerCase();
    if (lower.includes('cif') || lower.includes('nif')) return 'nif';
    if (lower.includes('autoriz')) return 'autorizacion';
    if (lower.includes('multipunto')) return 'multipunto';
    return null;
  };

  // Build a lookup by category from returned files, considering different shapes
  const lookup = {};
  itemsFiles.value.forEach(f => {
    const cat = f._category || mapFileTypeToCategory(f.file_type || f.fileType || f.fileTypeName) || null;
    const key = cat || `other_${f.id ?? Math.random()}`;
    lookup[key] = f;
  });

  return fileCategories.map(cat => {
    const found = lookup[cat.key] || itemsFiles.value.find(f => f._category === cat.key);
    const fname = found ? (found.file_name || found.name || found.filename || '-') : '-';
    const rawDate = found ? (found.created_at || found.fecha || found.date || null) : null;
    const fecha = rawDate ? formatDateEU(rawDate) : '-';
    const filePath = found ? (found.file_path || found.path || '') : '';
    const url = filePath
      ? (filePath.startsWith('http') ? filePath : `${import.meta.env.VITE_API_URL}/${filePath}`)
      : null;

    return {
      categoryKey: cat.key,
      fichero: cat.label,
      fecha,
      name: fname,
      accion: found ? 'Reemplazar' : 'Añadir',
      url,
      file_path: filePath,
      file_name: fname
    };
  });
});

const accountTypeString = computed(
  () => accountTypeLabel(userData.value, userData.value?.tipocuenta ?? '-')
);

const canEditObservations = computed(() => {
  if (detailKind.value !== 'account') return false;
  if (isAdsUser()) return true;

  const currentUserId = Number(localStorage.getItem('id'));
  return Number.isFinite(currentUserId) && (userData.value?.children ?? []).some(
    (member) => Number(member.id) === currentUserId && Boolean(member.is_primary)
  );
});

const {
  value: observations,
  saving: savingObservations,
  error: observationsError,
  changed: observationsChanged,
  status: observationsStatus,
  reset: resetObservations,
  save: saveObservations,
} = useAccountTextField({
  accountId: id,
  account: userData,
  field: 'observations',
  maxLength: 1000,
  messages: 'admin.observations',
  canEdit: canEditObservations,
  t,
});

// Los comentarios comerciales son internos: la API solo los envía al personal de ADS.
const canEditComments = computed(() => detailKind.value === 'account' && isAdsUser());

const {
  value: comments,
  saving: savingComments,
  error: commentsError,
  changed: commentsChanged,
  status: commentsStatus,
  reset: resetComments,
  save: saveComments,
} = useAccountTextField({
  accountId: id,
  account: userData,
  field: 'comments',
  maxLength: 2000,
  messages: 'admin.comments',
  canEdit: canEditComments,
  t,
});

const hasUnsavedText = computed(() => observationsChanged.value || commentsChanged.value);

function warnBeforeUnload(event) {
  if (!hasUnsavedText.value) return;
  event.preventDefault();
  event.returnValue = '';
}

onBeforeRouteLeave(() => {
  if (!hasUnsavedText.value) return true;
  return window.confirm(t(observationsChanged.value ? 'admin.observations.unsavedChanges' : 'admin.comments.unsavedChanges'));
});

// watch(currentUsersPage, (newPage) => {
//   fetchUsers();
// });
// const handlePageChangeUsers = (newPage) => {
//   currentUsersPage.value = newPage;
//   fetchUsers();
// };

// watch(currentFilesPage, (newPage) => {
//   fetchUsers();
// });
// const handlePageChangeFiles = (newPage) => {
//   currentFilesPage.value = newPage;
//   fetchUsers();
// };

// watch(currentBonosPage, (newPage) => {
//   fetchUsers();
// });
// const handlePageChangeBonos = (newPage) => {
//   currentBonosPage.value = newPage;
//   fetchUsers();
// };

const handleExportStarted = () => {
  console.log(t('admin.table.export.started'));
  exportStore.startExport('Exportando bonos...');
};

const handleExportCompleted = () => {
  console.log(t('admin.table.export.completed'));
  exportStore.completeExport();
};

const handleExportFailed = () => {
  console.log(t('admin.table.export.failed'));
  exportStore.failExport('Error al exportar bonos');
};

// File upload refs and handlers
const fileInputRef = ref(null);
const selectedCategory = ref(null);
const uploading = ref(false);

const triggerFileInput = (categoryKey) => {
  selectedCategory.value = categoryKey || null;
  // open the hidden file input
  if (fileInputRef.value && typeof fileInputRef.value.click === 'function') {
    fileInputRef.value.click();
  }
};

const onFileSelected = async (event) => {
  const files = event?.target?.files;
  if (!files || !files.length) return;
  const file = files[0];
  const category = selectedCategory.value || '';

  // Client-side validation (same rules as NewAdmin.vue)
  const allowedExt = ['jpg', 'jpeg', 'png', 'pdf'];
  const maxBytes = 2048 * 1024; // 2 MB
  const fileName = file.name || '';
  const ext = fileName.split('.').pop().toLowerCase();

  if (!allowedExt.includes(ext)) {
    toast.error('Tipo de archivo no permitido. Solo se permiten: jpg, jpeg, png, pdf.', { position: 'top-right' });
    if (fileInputRef.value) fileInputRef.value.value = '';
    selectedCategory.value = null;
    return;
  }

  if (file.size > maxBytes) {
    toast.error('El archivo excede el tamaño máximo de 2MB.', { position: 'top-right' });
    if (fileInputRef.value) fileInputRef.value.value = '';
    selectedCategory.value = null;
    return;
  }

  // Map table category to API file_type and file_name used in NewAdmin.vue
  const fileTypeMap = {
    nif: 'cif',
    autorizacion: 'autorizacion',
    multipunto: 'multipunto'
  };
  const fileNameMap = {
    nif: 'ficheroCif',
    autorizacion: 'autorizacion',
    multipunto: 'multipunto'
  };

  const file_type = fileTypeMap[category] || category || 'file';
  const file_name_key = fileNameMap[category] || category || 'file';

  const formData = new FormData();
  formData.append('file', file);
  formData.append('file_name', file_name_key);
  formData.append('file_path', file.name);
  formData.append('request_id', id);
  formData.append('file_type', file_type);

  uploading.value = true;
  try {
    const uploadUrl = `${import.meta.env.VITE_API_URL}/admin/file/upload/${id}`;
    const response = await axios.post(uploadUrl, formData, {
      headers: {
        'Accept': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
        // let axios set Content-Type for multipart/form-data
      }
    });

    console.log('Upload response:', response.data);

    if (response.data?.value) {
      toast.success('Archivo subido correctamente', { position: 'top-right' });
      // refresh files list
      await userFetchDetail();
    } else {
      toast.error('Error subiendo el archivo', { position: 'top-right' });
      console.error('Upload failed response:', response.data);
    }
  } catch (err) {
    console.error('Error uploading file:', err);
    toast.error('Error subiendo el archivo', { position: 'top-right' });
  } finally {
    uploading.value = false;
    // clear input so same file can be selected again if needed
    if (fileInputRef.value) fileInputRef.value.value = '';
    selectedCategory.value = null;
  }
};

const openFile = async (file) => {
  try {
    const path = file.file_path || '';
    if (!path) {
      toast.error('Archivo no disponible');
      return;
    }

    if (String(path).startsWith('http')) {
      window.open(file.url, '_blank');
      return;
    }

    const response = await axios.get(
      `${import.meta.env.VITE_API_URL}/export/download`,
      {
        headers: {
          'Accept': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        params: { path },
        responseType: 'blob'
      }
    );

    const blob = new Blob([response.data]);
    const url = window.URL.createObjectURL(blob);
    window.open(url, '_blank');
    setTimeout(() => window.URL.revokeObjectURL(url), 10000);
  } catch (err) {
    console.error('Error opening file:', err);
    toast.error('Error al abrir el archivo');
  }
};

</script>
